/**
 * Event Countdown Widget Component
 * Displays upcoming events with countdowns and tooltips
 */

class EventCountdownWidget {
    constructor(config = {}) {
        this.config = {
            serverId: config.serverId || 'default',
            maxEvents: config.maxEvents || 20,
            autoRefresh: config.autoRefresh !== false,
            refreshInterval: config.refreshInterval || 60000,
            collapsible: config.collapsible !== false,
            defaultCollapsed: config.defaultCollapsed || false,
            apiTimeout: config.apiTimeout || 10000,
            maxTooltipEvents: config.maxTooltipEvents || 5
        };

        this.dayNames = {
            1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday',
            5: 'Friday', 6: 'Saturday', 7: 'Sunday'
        };

        this.currentEvents = [];
        this.countdownIntervals = [];
        this.currentTooltip = null;
        this.isInitialized = false;
        this.refreshTimer = null;
        this.timeUpdateTimer = null;
        this.widget = null;
        this.eventsCache = new Map();
        this.lastFetchTime = 0;
        this.fetchPromise = null;
    }

    /**
     * Initialize the widget when DOM is ready
     */
    init() {
        // Check if widget exists
        this.widget = document.querySelector(`.event-countdown-widget[data-server-id="${this.config.serverId}"]`);
        if (!this.widget) {
            return; // Widget not present on this page
        }

        // Defer initialization to avoid blocking main thread
        requestIdleCallback ? 
            requestIdleCallback(() => this.initializeAsync()) : 
            setTimeout(() => this.initializeAsync(), 100);
    }

    /**
     * Asynchronous initialization
     */
    async initializeAsync() {
        try {
            if (this.isInitialized) return;

            await this.setupUI();
            await this.startServerTimeUpdates();
            await this.loadEvents();

            if (this.config.autoRefresh) {
                this.setupAutoRefresh();
            }

            this.setupEventListeners();
            this.isInitialized = true;
        } catch (error) {
            console.warn('Event countdown widget initialization failed:', error);
            this.showError('Widget initialization failed');
        }
    }

    /**
     * Setup widget UI
     */
    async setupUI() {
        const toggleButton = this.widget.querySelector(`#widget-toggle-${this.config.serverId}`);
        const widgetContent = this.widget.querySelector(`#widget-content-${this.config.serverId}`);

        // Initialize collapse state
        if (this.config.collapsible && this.config.defaultCollapsed) {
            this.widget.classList.add('collapsed');
        }

        // Handle toggle button
        if (toggleButton && widgetContent) {
            toggleButton.addEventListener('click', () => this.toggleCollapse());

            // Restore saved state
            try {
                const savedState = localStorage.getItem(`eventCountdownWidget_${this.config.serverId}_collapsed`);
                if (savedState !== null) {
                    const shouldCollapse = savedState === 'true';
                    this.widget.classList.toggle('collapsed', shouldCollapse);
                }
            } catch (e) {
                // Ignore localStorage errors
            }
        }
    }

    /**
     * Toggle widget collapse state
     */
    toggleCollapse() {
        const isCollapsed = this.widget.classList.contains('collapsed');
        this.widget.classList.toggle('collapsed');
        this.widget.setAttribute('data-collapsed', !isCollapsed);

        try {
            localStorage.setItem(`eventCountdownWidget_${this.config.serverId}_collapsed`, !isCollapsed);
        } catch (e) {
            // Ignore localStorage errors
        }
    }

    /**
     * Start server time updates
     */
    async startServerTimeUpdates() {
        this.updateServerTime();

        if (this.timeUpdateTimer) {
            clearInterval(this.timeUpdateTimer);
        }

        this.timeUpdateTimer = setInterval(() => this.updateServerTime(), 1000);
    }

    /**
     * Update server time display
     */
    updateServerTime() {
        try {
            const now = new Date();
            const timeString = now.toLocaleTimeString('pt-BR', {
                hour12: false,
                timeZone: 'America/Sao_Paulo'
            });

            const timeElement = this.widget.querySelector('#current-time');
            if (timeElement) {
                timeElement.textContent = timeString;
            }
        } catch (error) {
            // Silent fail for time updates
        }
    }

    /**
     * Setup auto-refresh
     */
    setupAutoRefresh() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }

        this.refreshTimer = setInterval(() => {
            this.loadEvents().catch(error => {
                console.warn('Auto-refresh error:', error);
            });
        }, this.config.refreshInterval);
    }

    /**
     * Setup event listeners (tooltip handled inline in template)
     */
    setupEventListeners() {
        // Tooltip functionality is now handled inline in the template
        // This method is kept for potential future event handling
    }

    /**
     * Load events from API with caching
     */
    async loadEvents() {
        try {
            // Use cached promise if request is in flight
            if (this.fetchPromise && Date.now() - this.lastFetchTime < 1000) {
                return await this.fetchPromise;
            }

            this.fetchPromise = this.fetchEvents();
            const events = await this.fetchPromise;
            
            this.currentEvents = Array.isArray(events) ? events : [];
            this.lastFetchTime = Date.now();

            // Use requestAnimationFrame for smooth rendering
            requestAnimationFrame(() => {
                this.displayEvents(this.currentEvents);
                this.hideError();
            });

        } catch (error) {
            console.warn('Event loading error:', error);
            this.showError('Unable to load events');
        } finally {
            this.fetchPromise = null;
        }
    }

    /**
     * Fetch events from API with timeout
     */
    async fetchEvents() {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.config.apiTimeout);

        try {
            const response = await fetch(`/api/events/schedule/${this.config.serverId}`, {
                signal: controller.signal,
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                throw new Error('Request timeout');
            }
            throw error;
        }
    }

    /**
     * Display events in the widget
     */
    displayEvents(events) {
        const container = this.widget.querySelector(`#events-list-${this.config.serverId}`);
        if (!container) return;

        // Clear existing intervals
        this.clearCountdowns();

        if (!events || events.length === 0) {
            container.innerHTML = `
                <div class="no-events p-4 text-center">
                    <i class="fas fa-calendar-times text-2xl mb-2 x-text-muted"></i>
                    <p class="x-text-secondary">No events configured</p>
                </div>
            `;
            return;
        }

        const upcomingEvents = this.getUpcomingEvents(events, this.config.maxEvents);

        // Use DocumentFragment for better performance
        const fragment = document.createDocumentFragment();
        const tempContainer = document.createElement('div');

        upcomingEvents.forEach((eventData, index) => {
            const eventElement = this.createEventElement(eventData, index);
            tempContainer.appendChild(eventElement);
        });

        container.innerHTML = '';
        container.appendChild(tempContainer);

        // Start countdowns
        this.startCountdowns(upcomingEvents);
    }

    /**
     * Create event element with Tailwind tooltip
     */
    createEventElement(eventData, index) {
        const isNext = index === 0;
        const div = document.createElement('div');
        div.className = 'event-item x-item px-4 py-3 flex items-center justify-between hover:bg-opacity-80 transition-all duration-200 relative group';
        div.dataset.eventName = eventData.event.name;
        div.dataset.eventType = eventData.type;
        div.dataset.eventData = JSON.stringify(eventData.event);

        // Get next occurrences for tooltip
        const nextOccurrences = this.getEventOccurrences(eventData.event, 5);
        let tooltipContent = '';
        
        if (nextOccurrences.length > 0) {
            tooltipContent = nextOccurrences.map((occurrence, idx) => {
                const isFirst = idx === 0;
                return `
                    <div class="flex items-center justify-between py-1 ${isFirst ? 'text-green-400 font-semibold' : 'text-gray-300'}">
                        <span class="flex items-center">
                            <svg class="w-3 h-3 mr-1 opacity-70" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clip-rule="evenodd"></path>
                            </svg>
                            ${occurrence.day ? `${occurrence.day} ` : ''}${occurrence.time}
                        </span>
                        <span class="text-xs opacity-80">${occurrence.fromNow}</span>
                    </div>
                `;
            }).join('');
        }

        div.innerHTML = `
            <div class="event-info flex-1 mt-2">
                <div class="x-text-primary text-sm font-medium">
                    ${eventData.event.name}
                </div>
                <div class="x-text-secondary text-xs">
                    <i class="fas fa-clock mr-1"></i>
                    ${eventData.nextTime}
                    ${eventData.nextDay ? ` - ${eventData.nextDay}` : ''}
                </div>
            </div>
            
            <div class="countdown-display text-right">
                <div class="countdown ${isNext ? 'x-countdown-active ' : 'x-text-accent '}text-sm font-mono" 
                     id="countdown-${this.config.serverId}-${index}">
                    --:--:--
                </div>
                ${isNext ? '<div class="x-text-success text-xs font-medium">next</div>' : ''}
            </div>

            <!-- Tailwind Tooltip -->
            <div class="absolute left-0 transform -translate-x-full -translate-y-2 ml-2 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-300 z-50 pointer-events-none">
                <div class="bg-gray-800 text-white text-xs rounded-lg p-3 shadow-xl border border-gray-700 min-w-64 max-w-80">
                    <div class="font-semibold mb-2 flex items-center">
                        <div class="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        ${eventData.event.name}
                    </div>
                    <div class="text-gray-300 text-xs uppercase tracking-wide mb-2">Next 5 occurrences:</div>
                    <div class="space-y-1">
                        ${tooltipContent}
                    </div>
                    <!-- Arrow -->
                    <div class="absolute top-4 -right-2 w-4 h-4 bg-gray-800 border-r border-b border-gray-700 transform rotate-45"></div>
                </div>
            </div>
        `;

        return div;
    }

    /**
     * Get upcoming events sorted by next occurrence (one per event name)
     */
    getUpcomingEvents(events, maxCount) {
        const now = new Date();
        const today = now.getDay() || 7;
        const currentTime = now.getHours().toString().padStart(2, '0') + ':' + 
                           now.getMinutes().toString().padStart(2, '0');

        const upcomingEvents = [];
        const eventMap = new Map(); // Track events by name to avoid duplicates

        events.forEach(event => {
            if (event.time && Array.isArray(event.time)) {
                // Daily events - find next occurrence
                let nextOccurrence = null;
                
                event.time.forEach(time => {
                    const eventDateTime = new Date();
                    const [hours, minutes] = time.split(':');
                    eventDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

                    if (time <= currentTime) {
                        eventDateTime.setDate(eventDateTime.getDate() + 1);
                    }

                    if (!nextOccurrence || eventDateTime < nextOccurrence.nextDateTime) {
                        nextOccurrence = {
                            event: event,
                            nextTime: time,
                            nextDateTime: eventDateTime,
                            type: 'daily'
                        };
                    }
                });

                if (nextOccurrence && !eventMap.has(event.name)) {
                    eventMap.set(event.name, nextOccurrence);
                    upcomingEvents.push(nextOccurrence);
                }
            } else if (event.schedule && typeof event.schedule === 'object') {
                // Weekly events - find next occurrence
                let nextOccurrence = null;

                Object.entries(event.schedule).forEach(([day, times]) => {
                    const eventDay = parseInt(day);
                    times.forEach(time => {
                        const eventDateTime = new Date();
                        const [hours, minutes] = time.split(':');

                        let daysUntil = eventDay - today;
                        if (daysUntil < 0 || (daysUntil === 0 && time <= currentTime)) {
                            daysUntil += 7;
                        }

                        eventDateTime.setDate(eventDateTime.getDate() + daysUntil);
                        eventDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

                        if (!nextOccurrence || eventDateTime < nextOccurrence.nextDateTime) {
                            nextOccurrence = {
                                event: event,
                                nextTime: time,
                                nextDateTime: eventDateTime,
                                nextDay: this.dayNames[eventDay],
                                type: 'weekly'
                            };
                        }
                    });
                });

                if (nextOccurrence && !eventMap.has(event.name)) {
                    eventMap.set(event.name, nextOccurrence);
                    upcomingEvents.push(nextOccurrence);
                }
            }
        });

        return upcomingEvents
            .sort((a, b) => a.nextDateTime - b.nextDateTime)
            .slice(0, maxCount);
    }

    /**
     * Start countdown timers
     */
    startCountdowns(upcomingEvents) {
        upcomingEvents.forEach((eventData, index) => {
            const updateCountdown = () => this.updateCountdown(index, eventData.nextDateTime);
            
            // Update immediately
            updateCountdown();
            
            // Setup interval
            const interval = setInterval(updateCountdown, 1000);
            this.countdownIntervals.push(interval);
        });
    }

    /**
     * Update countdown display
     */
    updateCountdown(index, targetDateTime) {
        const countdownElement = this.widget.querySelector(`#countdown-${this.config.serverId}-${index}`);
        if (!countdownElement) return;

        const now = new Date();
        const timeDiff = targetDateTime - now;

        if (timeDiff <= 0) {
            countdownElement.textContent = '00:00:00';
            // Reload events after a delay
            setTimeout(() => this.loadEvents(), 1000);
            return;
        }

        const hours = Math.floor(timeDiff / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

        countdownElement.textContent = 
            hours.toString().padStart(2, '0') + ':' +
            minutes.toString().padStart(2, '0') + ':' +
            seconds.toString().padStart(2, '0');
    }


    /**
     * Get next N occurrences of an event
     */
    getEventOccurrences(event, count) {
        const now = new Date();
        const occurrences = [];

        if (event.time && Array.isArray(event.time)) {
            // Daily events - get all times for multiple days
            let currentDate = new Date();
            let dayOffset = 0;

            while (occurrences.length < count && dayOffset < 7) {
                const dayStart = new Date(currentDate);
                dayStart.setHours(0, 0, 0, 0);

                event.time.forEach(time => {
                    if (occurrences.length >= count) return;

                    const eventDate = new Date(dayStart);
                    const [hours, minutes] = time.split(':').map(Number);
                    eventDate.setHours(hours, minutes, 0, 0);

                    // Only include future events
                    if (eventDate > now) {
                        let dayLabel = '';
                        if (dayOffset === 0) {
                            dayLabel = 'Today';
                        } else if (dayOffset === 1) {
                            dayLabel = 'Tomorrow';
                        } else {
                            dayLabel = eventDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                        }

                        occurrences.push({
                            time: time,
                            date: eventDate,
                            day: dayLabel,
                            fromNow: this.getRelativeTime(eventDate)
                        });
                    }
                });

                dayOffset++;
                currentDate.setDate(currentDate.getDate() + 1);
            }
        } else if (event.schedule && typeof event.schedule === 'object') {
            // Weekly events - get occurrences for multiple weeks
            const today = now.getDay() || 7; // Convert Sunday (0) to 7
            const currentTime = now.getHours().toString().padStart(2, '0') + ':' + 
                               now.getMinutes().toString().padStart(2, '0');

            let weekOffset = 0;

            while (occurrences.length < count && weekOffset < 4) {
                Object.entries(event.schedule).forEach(([dayStr, times]) => {
                    const day = parseInt(dayStr);
                    
                    times.forEach(time => {
                        if (occurrences.length >= count) return;

                        let daysUntil = day - today;
                        
                        // If it's today but time has passed, or if day is in the past, go to next week
                        if (daysUntil < 0 || (daysUntil === 0 && time <= currentTime)) {
                            daysUntil += 7;
                        }
                        
                        // Add weeks offset
                        daysUntil += (weekOffset * 7);

                        const eventDate = new Date(now);
                        eventDate.setDate(eventDate.getDate() + daysUntil);
                        const [hours, minutes] = time.split(':').map(Number);
                        eventDate.setHours(hours, minutes, 0, 0);

                        let dayLabel = '';
                        if (daysUntil === 0) {
                            dayLabel = 'Today';
                        } else if (daysUntil === 1) {
                            dayLabel = 'Tomorrow';
                        } else if (daysUntil < 7) {
                            dayLabel = this.dayNames[day];
                        } else {
                            dayLabel = `${this.dayNames[day]} (${eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
                        }

                        occurrences.push({
                            time: time,
                            date: eventDate,
                            day: dayLabel,
                            fromNow: this.getRelativeTime(eventDate)
                        });
                    });
                });

                weekOffset++;
            }
        }

        return occurrences
            .sort((a, b) => a.date - b.date)
            .slice(0, count);
    }

    /**
     * Get next occurrence of a weekday
     */
    getNextWeekdayDate(targetDay, weekOffset = 0) {
        const date = new Date();
        const currentDay = date.getDay() || 7;
        let daysUntil = targetDay - currentDay;

        if (daysUntil < 0 || weekOffset > 0) {
            daysUntil += 7 * (weekOffset + (daysUntil < 0 ? 1 : 0));
        }

        date.setDate(date.getDate() + daysUntil);
        return date;
    }

    /**
     * Get relative time string
     */
    getRelativeTime(date) {
        const now = new Date();
        const diff = date - now;
        
        if (diff <= 0) return 'now';
        
        const minutes = Math.floor(diff / (1000 * 60));
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days > 7) {
            const weeks = Math.floor(days / 7);
            return weeks === 1 ? 'in 1 week' : `in ${weeks} weeks`;
        }
        if (days > 1) return `in ${days} days`;
        if (days === 1) return 'tomorrow';
        if (hours > 1) return `in ${hours}h`;
        if (hours === 1) return 'in 1h';
        if (minutes > 30) return `in ${minutes}m`;
        if (minutes > 1) return `in ${minutes}m`;
        if (minutes === 1) return 'in 1m';
        return 'soon';
    }


    /**
     * Show error state
     */
    showError(message) {
        const errorElement = this.widget.querySelector(`#error-state-${this.config.serverId}`);
        const listElement = this.widget.querySelector(`#events-list-${this.config.serverId}`);

        if (errorElement && listElement) {
            const errorText = errorElement.querySelector('.error-text');
            if (errorText) {
                errorText.textContent = message;
            }
            errorElement.classList.remove('hidden');
            listElement.classList.add('hidden');
        }
    }

    /**
     * Hide error state
     */
    hideError() {
        const errorElement = this.widget.querySelector(`#error-state-${this.config.serverId}`);
        const listElement = this.widget.querySelector(`#events-list-${this.config.serverId}`);

        if (errorElement && listElement) {
            errorElement.classList.add('hidden');
            listElement.classList.remove('hidden');
        }
    }

    /**
     * Clear all countdown intervals
     */
    clearCountdowns() {
        this.countdownIntervals.forEach(interval => {
            try {
                clearInterval(interval);
            } catch (e) {
                // Ignore errors
            }
        });
        this.countdownIntervals = [];
    }

    /**
     * Destroy widget and clean up
     */
    destroy() {
        this.clearCountdowns();

        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = null;
        }

        if (this.timeUpdateTimer) {
            clearInterval(this.timeUpdateTimer);
            this.timeUpdateTimer = null;
        }

        this.currentEvents = [];
        this.eventsCache.clear();
        this.isInitialized = false;
    }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    // Find all event countdown widgets on the page
    const widgets = document.querySelectorAll('.event-countdown-widget');
    
    widgets.forEach(widgetElement => {
        const config = {
            serverId: widgetElement.dataset.serverId || 'default',
            autoRefresh: widgetElement.dataset.autoRefresh !== 'false',
            refreshInterval: parseInt(widgetElement.dataset.refreshInterval) || 60000,
            defaultCollapsed: widgetElement.dataset.collapsed === 'true'
        };

        const widget = new EventCountdownWidget(config);
        widget.init();

        // Store widget instance for potential cleanup
        widgetElement._widgetInstance = widget;
    });
});

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
    const widgets = document.querySelectorAll('.event-countdown-widget');
    widgets.forEach(widgetElement => {
        if (widgetElement._widgetInstance) {
            widgetElement._widgetInstance.destroy();
        }
    });
});

export default EventCountdownWidget;